import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { FOUNDATION } from '../config/foundation';

export function Terms() {
  usePageTitle('Terms & Conditions');
  return (
    <Layout>
      <PageHero eyebrow="Legal" title={<>Terms &amp; Conditions</>}>
        Last updated March 2026
      </PageHero>
      <Reveal className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <div className="prose prose-sm max-w-none text-mustard-charcoal/80 space-y-6">
          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">1. About these terms</h2>
            <p>These terms and conditions govern your use of the LifeSpark360&#8482; platform ("the Platform"), operated by {FOUNDATION.name}, a {FOUNDATION.structure} registered in England and Wales (Charity No. {FOUNDATION.charityNumber}) ("the Foundation", "we", "us"). By using the Platform, you agree to be bound by these terms.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">2. The service</h2>
            <p>The Platform provides digital programme delivery for the LifeSpark360 and Seedbed coaching programmes. This includes:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li>Online readiness assessments</li>
              <li>AI-powered CV analysis and feedback</li>
              <li>Structured coaching session management</li>
              <li>Digital workbook and resource delivery</li>
              <li>Progress tracking and micro-action management</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">3. Account registration</h2>
            <p>To access programme features, you must register an account. You agree to:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorised access</li>
              <li>Accept responsibility for all activity under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">4. Programme places and eligibility</h2>
            <p>As a registered charity, we provide our programmes free of charge to eligible participants, funded by donations and grants. Places are subject to availability and to our eligibility and allocation criteria. Where demand exceeds the places available, we prioritise those facing the greatest barriers.</p>
            <p className="mt-2"><strong>Withdrawal:</strong> You may withdraw from a programme at any time by contacting us. We ask that you let us know as early as possible so that your place can be offered to someone else on our waiting list.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">5. Coaching relationship</h2>
            <p>LifeSpark360 and Seedbed are career coaching programmes. They are not therapy, counselling, or regulated financial advice. Your assigned trainer is a certified LifeSpark360 practitioner, not a licensed therapist. If you require mental health support, please contact an appropriate professional.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">6. Intellectual property</h2>
            <p>All content on the Platform — including the SPARK Method, programme materials, workbooks, assessment questions, templates, and branding — is the intellectual property of {FOUNDATION.name}. LifeSpark360&#8482; is a registered trademark. You may not reproduce, distribute, or commercially exploit any content without written permission.</p>
            <p className="mt-2">Content you submit (assessment responses, workbook entries, CV files) remains your intellectual property. By submitting content, you grant us a licence to process it for the purpose of delivering the service.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">7. AI-powered features</h2>
            <p>The CV Assessor uses artificial intelligence to analyse your CV. AI-generated feedback is provided as guidance and should not be treated as professional career advice. We do not guarantee the accuracy, completeness, or suitability of AI-generated content. You should review all AI feedback critically before acting on it.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">8. Acceptable use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc ml-6 space-y-1 mt-2">
              <li>Share your account credentials with others</li>
              <li>Upload malicious files or content</li>
              <li>Attempt to access other users' data</li>
              <li>Use the Platform for any unlawful purpose</li>
              <li>Reverse-engineer or scrape the Platform</li>
              <li>Share programme materials with non-enrolled individuals</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">9. Limitation of liability</h2>
            <p>The Platform is provided "as is". While we strive for reliability and accuracy, we do not guarantee uninterrupted access or error-free operation. To the maximum extent permitted by law, {FOUNDATION.name} shall not be liable for any indirect, incidental, or consequential damages arising from use of the Platform.</p>
            <p className="mt-2">Nothing in these terms limits or excludes our liability where it would be unlawful to do so, including liability for death or personal injury caused by negligence, or for fraud.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">10. Changes to these terms</h2>
            <p>We may update these terms from time to time. We will notify registered users of material changes by email. Continued use of the Platform after notification constitutes acceptance of the updated terms.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">11. Governing law</h2>
            <p>These terms are governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mt-8 mb-3">12. Contact</h2>
            <p><strong>{FOUNDATION.name}</strong><br />Email: {FOUNDATION.email}<br />{FOUNDATION.structure} &middot; Charity No. {FOUNDATION.charityNumber}</p>
          </section>
        </div>
      </Reveal>
    </Layout>
  );
}
